import React from 'react';
import { Fireworks } from '@fireworks-js/react';
import {
  Box,
  Divider,
  Heading,
  SimpleGrid,
  Text,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/core';

const VOTE_VALUES = [1, 2, 3, 5, 8, 13];

const allEqual = (arr: number[]) => arr.every(val => val === arr[0]);

interface ResultsDisplayProps {
  votes: number[];
}

const ResultsDisplay = ({ votes }: ResultsDisplayProps) => {
  const activeBg = useColorModeValue('purple.50', 'purple.900');
  const activeBorder = useColorModeValue('purple.300', 'purple.500');
  const inactiveBorder = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const avgBg = useColorModeValue('purple.50', 'purple.900');

  const avg =
    votes.length > 0
      ? (votes.reduce((pv, cv) => pv + cv) / votes.length).toFixed(1)
      : null;
  const min = votes.length > 0 ? Math.min(...votes) : null;
  const max = votes.length > 0 ? Math.max(...votes) : null;
  const consensus = votes.length > 0 && allEqual(votes);

  return (
    <>
      <Box p={5} shadow="md" borderWidth="1px" borderRadius="xl">
        <Heading fontSize="md" mb={4}>
          Results
        </Heading>

        {/* Vote distribution cards */}
        <Wrap direction="row" spacing={3}>
          {VOTE_VALUES.map(score => {
            const numVotes = votes.filter(vote => vote === score).length;
            const pct = votes.length > 0 ? (numVotes / votes.length) * 100 : 0;
            return (
              <WrapItem key={score}>
                <Box
                  px={4}
                  pt={3}
                  pb={2}
                  borderRadius="xl"
                  borderWidth="2px"
                  borderColor={numVotes > 0 ? activeBorder : inactiveBorder}
                  bg={numVotes > 0 ? activeBg : 'transparent'}
                  textAlign="center"
                  minW={14}
                  opacity={numVotes > 0 ? 1 : 0.35}
                  transition="all 0.2s">
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={numVotes > 0 ? 'purple.500' : 'gray.400'}>
                    {score}
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={numVotes > 0 ? 'purple.400' : 'gray.500'}>
                    {numVotes > 0 ? `×${numVotes}` : '·'}
                  </Text>
                  {numVotes > 0 && (
                    <Box mt={2} h="3px" bg="purple.100" borderRadius="full">
                      <Box
                        h="full"
                        bg="purple.400"
                        borderRadius="full"
                        w={`${pct}%`}
                      />
                    </Box>
                  )}
                </Box>
              </WrapItem>
            );
          })}
        </Wrap>

        {votes.length > 0 && (
          <>
            <Divider my={5} />
            <SimpleGrid columns={3} spacing={4}>
              {/* Min */}
              <Box textAlign="center" p={4} borderRadius="lg" bg={statBg}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={1}>
                  Low
                </Text>
                <Text fontSize="3xl" fontWeight="bold" color="blue.400">
                  {min}
                </Text>
              </Box>

              {/* Average — center, more prominent */}
              <Box
                textAlign="center"
                p={4}
                borderRadius="lg"
                bg={avgBg}
                borderWidth="2px"
                borderColor={activeBorder}
                position="relative">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="purple.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={1}>
                  {consensus ? '🎉 Consensus' : 'Average'}
                </Text>
                <Text fontSize="3xl" fontWeight="bold" color="purple.500">
                  {avg}
                </Text>
              </Box>

              {/* Max */}
              <Box textAlign="center" p={4} borderRadius="lg" bg={statBg}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={1}>
                  High
                </Text>
                <Text fontSize="3xl" fontWeight="bold" color="blue.400">
                  {max}
                </Text>
              </Box>
            </SimpleGrid>
          </>
        )}
      </Box>

      {consensus && (
        <Fireworks
          options={{ opacity: 0.5 }}
          style={{
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            position: 'fixed',
            background: 'inherit',
            zIndex: -1,
          }}
        />
      )}
    </>
  );
};

export default ResultsDisplay;
